import { createContext, useContext, useState, useEffect, useCallback } from "react";
import api from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { UpgradeModal } from "@/components/UpgradeModal";

const TierContext = createContext(null);

export const useTier = () => useContext(TierContext);

export function TierProvider({ children }) {
  const { user } = useAuth();
  const [tier, setTier] = useState(() => localStorage.getItem("dd_tier") || "free");
  const [status, setStatus] = useState("none");
  const [currentPeriodEnd, setCurrentPeriodEnd] = useState(null);
  const [showPremiumWelcome, setShowPremiumWelcome] = useState(false);
  const [spotlights, setSpotlights] = useState([]);
  const [trialEligible, setTrialEligible] = useState(false);
  const [trialing, setTrialing] = useState(false);
  const [trialEnd, setTrialEnd] = useState(null);
  const [upgradeOpen, setUpgradeOpen] = useState(false);
  const [feature, setFeature] = useState(null);
  const [headline, setHeadline] = useState(null);

  const refresh = useCallback(async () => {
    try {
      const { data } = await api.get("/billing/status");
      setTier(data.tier);
      setStatus(data.status);
      setCurrentPeriodEnd(data.current_period_end);
      setShowPremiumWelcome(!!data.show_premium_welcome);
      setSpotlights(data.spotlights || []);
      setTrialEligible(!!data.trial_eligible);
      setTrialing(!!data.trialing);
      setTrialEnd(data.trial_end || null);
      localStorage.setItem("dd_tier", data.tier);
      return data;
    } catch {
      // Never crash — keep last known tier (or free).
      return null;
    }
  }, []);

  // Refresh whenever the authenticated user changes (handles SPA login without a
  // page reload) and reset to free on logout.
  const userId = user?.user_id;
  useEffect(() => {
    if (userId) {
      refresh();
    } else {
      setTier("free"); setStatus("none"); setCurrentPeriodEnd(null);
      setShowPremiumWelcome(false); setSpotlights([]);
      localStorage.setItem("dd_tier", "free");
    }
  }, [userId, refresh]);

  const openUpgrade = useCallback((f, opts) => {
    setFeature(f || null);
    setHeadline(opts?.headline || null);
    setUpgradeOpen(true);
  }, []);

  // Global 402 interceptor (api.js) dispatches this event.
  useEffect(() => {
    const handler = (e) => openUpgrade(e.detail?.feature);
    window.addEventListener("dd:premium-required", handler);
    return () => window.removeEventListener("dd:premium-required", handler);
  }, [openUpgrade]);

  return (
    <TierContext.Provider
      value={{ tier, status, currentPeriodEnd, showPremiumWelcome, spotlights, trialEligible, trialing, trialEnd, isPremium: tier === "premium", refresh, openUpgrade }}
    >
      {children}
      <UpgradeModal open={upgradeOpen} onOpenChange={setUpgradeOpen} feature={feature} headline={headline} />
    </TierContext.Provider>
  );
}
